import { defaultPageSize } from '@bioliberty/core';
import {
  ColumnDef,
  ColumnSort,
  ExpandedState,
  PaginationState,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { cn } from '../../util';
import { IconButton } from '../Button/IconButton';
import { Icon } from '../Icon';
import { Spinner } from '../Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { expandableHeader } from './headers';

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  initialSorting?: ColumnSort[];
  manualSorting?: boolean;
  enableMultiSort?: boolean;
  page?: number;
  pageSize?: number;
  pageCount?: number;
  expandable?: boolean;
  onRowClick?: (row: Row<T>) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onPaginationChange?: (pagination: PaginationState) => void;
  onExpandedChange?: (expanded: ExpandedState) => void;
  renderSubComponent?: (row: Row<T>) => React.ReactNode;
  autoResetPageIndex?: boolean;
  isLoading?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string;
  className?: string;
  testId?: string;
}

export const DataTable = ({
  columns,
  rows,
  initialSorting,
  manualSorting = false,
  enableMultiSort = false,
  page = 0,
  pageSize = defaultPageSize,
  pageCount,
  expandable = false,
  onRowClick,
  onSortingChange,
  onPaginationChange,
  onExpandedChange,
  renderSubComponent,
  autoResetPageIndex,
  isLoading = false,
  stickyHeader = false,
  maxHeight,
  className,
  testId
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: DataTableProps<any>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: page,
    pageSize
  });

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const tableColumns = useMemo(() => {
    return expandable ? [expandableHeader, ...columns] : columns;
  }, [columns, expandable]);

  if (autoResetPageIndex === false && pageCount === undefined && rows.length > 0) {
    const lastPageIndex = Math.max(0, Math.ceil(rows.length / pagination.pageSize) - 1);
    if (pagination.pageIndex > lastPageIndex) {
      setPagination({ ...pagination, pageIndex: lastPageIndex });
    }
  }

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    manualSorting,
    enableMultiSort,
    manualPagination: pageCount !== undefined,
    pageCount,
    autoResetPageIndex,
    getPaginationRowModel: pageCount ? undefined : getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: renderSubComponent ? undefined : (row) => row.children,
    getRowCanExpand: renderSubComponent ? () => true : undefined,
    onSortingChange: (updater) => {
      const value = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange?.(value);
      setSorting(value);
    },
    onPaginationChange: (updater) => {
      const value = typeof updater === 'function' ? updater(pagination) : updater;
      onPaginationChange?.(value);
      setPagination(value);
    },
    onExpandedChange: (updater) => {
      const value = typeof updater === 'function' ? updater(expanded) : updater;
      onExpandedChange?.(value);
      setExpanded(value);
    },
    defaultColumn: {
      minSize: 0,
      size: 0
    },
    state: {
      sorting,
      pagination,
      expanded
    }
  });

  return (
    <div data-testid={testId}>
      <div
        className={cn(className, stickyHeader && 'relative')}
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      >
        <Table>
          <TableHeader className={cn(stickyHeader && 'bg-primary sticky top-0 z-10 shadow-md')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        width: header.getSize() !== 0 ? header.getSize() : undefined
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {!isLoading &&
              (table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow
                      onClick={() => onRowClick?.(row)}
                      className={cn(onRowClick && 'hover:bg-highlight/20 cursor-pointer')}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: cell.column.getSize() !== 0 ? cell.column.getSize() : undefined
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && renderSubComponent && (
                      <TableRow>
                        <td colSpan={row.getVisibleCells().length}>{renderSubComponent(row)}</td>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={tableColumns.length}>
                    <div className="flex items-center justify-center">
                      <Icon type={Info} className="me-2" />
                      <span>No results</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}

            {isLoading && (
              <TableRow>
                <TableCell colSpan={tableColumns.length}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {!isLoading && table.getPageCount() > 1 && (
        <div className="mt-2 flex items-center justify-center">
          <IconButton
            icon={ChevronsLeft}
            onClick={() => {
              table.firstPage();
              table.resetExpanded();
            }}
            disabled={!table.getCanPreviousPage()}
            variant="transparent"
          />
          <IconButton
            icon={ChevronLeft}
            onClick={() => {
              table.previousPage();
              table.resetExpanded();
            }}
            disabled={!table.getCanPreviousPage()}
            variant="transparent"
          />
          <IconButton
            icon={ChevronRight}
            onClick={() => {
              table.nextPage();
              table.resetExpanded();
            }}
            disabled={!table.getCanNextPage()}
            variant="transparent"
          />
          <IconButton
            icon={ChevronsRight}
            onClick={() => {
              table.lastPage();
              table.resetExpanded();
            }}
            disabled={!table.getCanNextPage()}
            variant="transparent"
          />

          <span className="ms-4 flex items-center gap-1">
            <div>Page</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
            </strong>
          </span>
        </div>
      )}
    </div>
  );
};
